import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { KeyRound, LogOut, Menu, PanelLeftClose, PanelLeftOpen, Search } from 'lucide-react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useApprovalQueuePendingCount } from '../api/approvals.ts';
import { OpsApiError, opsApiRequest } from '../api/client.ts';
import { useOpsRisks } from '../api/risks.ts';
import { APPROVAL_QUEUE_ROLES, ROLE_OPS_ADMIN, expandOpsRoles } from '../auth/opsRoles.ts';
import { BrandLogo } from '../components/BrandLogo.tsx';
import { ChangePasswordModal } from '../components/ChangePasswordModal.tsx';
import { formatLocalTimestamp } from '../components/formatters.ts';
import { useSession } from '../hooks/useSession.tsx';
import { useIsMobile } from '../hooks/useViewport.ts';
import { LocaleSwitcher, useT } from '../i18n/index.ts';
import { Chip, Drawer, Icon, IconButton, StatusLamp, Tooltip, cx } from '../ui/index.ts';
import { ShellCommandPalette } from './ShellCommandPalette.tsx';
import { useQuotaBypass } from '../api/useRoutingLoad.ts';
import { canAccessRoles, OPS_HOME_PATH, titleItemForPath, visibleNavGroups } from './navigation.ts';
import type { NavGroup } from './navigation.ts';
import { pushRecentPath } from './recentPaths.ts';

/**
 * Dispatch Deck app chrome (epic #835 PR2) — sidebar + header + main.
 *
 *   - Sidebar: 224px, collapsible to a 56px icon rail (persisted in
 *     `ops.sidebar.rail`); below lg it becomes a Drawer behind the
 *     header hamburger (the old 224px-tall horizontal strip is gone).
 *   - Header: page title, ⌘K command palette trigger (a REAL search —
 *     the decorative input is gone), risk chip fed by the unified
 *     `/v1/ops/risks` endpoint (admin only; renders nothing while
 *     loading or on error), locale switcher. The notification bell had
 *     no source and is removed (DESIGN.md §1 "zero dead controls").
 *   - Every visited nav destination is recorded for the palette's
 *     "recent" group.
 *
 * `data-testid` values (`shell-sidebar`, `shell-nav-*`,
 * `shell-page-title`, `shell-email`, `shell-logout`,
 * `shell-mobile-nav`, `shell-mobile-nav-open`, `shell-cmdk-trigger`,
 * `shell-cmdk-trigger-mobile`, `shell-approvals-chip`, `shell-risk-*`,
 * `shell-home-link`, `shell-mobile-change-password`,
 * `shell-mobile-locale`, `shell-mobile-logout`) are stable so
 * existing E2E/RTL selectors keep working.
 */
interface Props {
  children?: ReactNode;
}

const RAIL_STORAGE_KEY = 'ops.sidebar.rail';

function loadRailPreference(): boolean {
  try {
    return window.localStorage.getItem(RAIL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function Shell({ children }: Props): JSX.Element {
  const isMobile = useIsMobile();
  const { user, clearUser, fetchImpl } = useSession();
  const location = useLocation();
  const t = useT();
  const rawRoles = user?.roles ?? [];
  const navGroups = useMemo(() => visibleNavGroups(rawRoles), [rawRoles]);
  const isAdmin = canAccessRoles(rawRoles, [ROLE_OPS_ADMIN]);
  const isApprover = canAccessRoles(rawRoles, APPROVAL_QUEUE_ROLES);

  const [rail, setRail] = useState(loadRailPreference);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  // Single source for "where am I": the longest role-visible nav match
  // (same logic as the header title). Nav highlighting derives from this
  // so nested routes (/crm/requests) light exactly
  // ONE item — prefix-matching NavLinks lit every ancestor at once.
  const activeNavItem = useMemo(
    () => titleItemForPath(rawRoles, location.pathname),
    [location.pathname, rawRoles],
  );
  const activeNavPath = activeNavItem?.to ?? null;
  const pageTitle =
    location.pathname === OPS_HOME_PATH
      ? t('ops_home.title')
      : activeNavItem !== null
        ? t(activeNavItem.labelKey)
        : t('shell.page.console_fallback');

  // Record nav destinations for the palette's "recent" group.
  useEffect(() => {
    if (activeNavPath !== null) {
      pushRecentPath(activeNavPath);
    }
    // Close the mobile drawer after any navigation.
    setMobileNavOpen(false);
  }, [activeNavPath, location.pathname]);

  // Global ⌘K / Ctrl+K.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setPaletteOpen((prev) => !prev);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  function toggleRail(): void {
    setRail((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(RAIL_STORAGE_KEY, next ? '1' : '0');
      } catch {
        // Preference only.
      }
      return next;
    });
  }

  async function handleLogout(): Promise<void> {
    try {
      await opsApiRequest<{ ok: boolean }>('/v1/auth/logout', { method: 'POST' }, fetchImpl);
    } catch (err) {
      // Even if /logout fails we clear the client-side mirror so the UI
      // doesn't stay in an inconsistent "logged in" state. The cookie
      // will expire on its own server-side TTL.
      if (!(err instanceof OpsApiError)) {
        throw err;
      }
    }
    clearUser();
  }

  const displayIdentity =
    user?.displayName !== undefined && user.displayName.length > 0
      ? user.displayName
      : user?.email !== undefined && user.email.length > 0
        ? user.email
        : t('shell.signed_in_fallback');
  const primaryRole = expandOpsRoles(rawRoles)[0];

  // Feishu-SSO accounts have no password, so there is nothing for the form
  // to change — ops-api answers such an attempt with a flat
  // INVALID_CREDENTIALS, which reads as "you typed it wrong" rather than
  // "this button was never for you". Hide the control instead
  // (DESIGN.md §1: zero dead controls).
  const canChangePassword = user?.hasPassword === true;

  return (
    <div className="flex h-screen max-md:h-dvh overflow-hidden bg-canvas text-paper">
      {/* Desktop sidebar */}
      <aside
        data-testid="shell-sidebar"
        className={cx(
          'hidden min-h-0 shrink-0 flex-col border-r border-line-soft bg-well transition-[width] duration-200 lg:flex',
          rail ? 'w-14' : 'w-56',
        )}
      >
        <div className={cx('flex shrink-0 items-center gap-2.5 px-3 pb-4 pt-5', rail && 'justify-center px-0')}>
          <Link
            to="/"
            aria-label={t('ops_home.title')}
            data-testid="shell-home-link"
            className={cx(
              'flex min-w-0 items-center gap-2.5 text-paper no-underline',
              rail && 'justify-center',
            )}
          >
            <BrandLogo className="h-8 w-8 shrink-0 text-paper" />
            {!rail ? (
              <div className="flex min-w-0 flex-col">
                <span className="block truncate text-section text-paper">{t('shell.brand.title')}</span>
                <span className="mt-0.5 block truncate font-mono text-micro text-paper-low">
                  {t('shell.brand.subtitle')}
                </span>
              </div>
            ) : null}
          </Link>
        </div>
        <nav
          aria-label={t('shell.nav.aria_label')}
          className="flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-2 pb-2"
        >
          <NavList groups={navGroups} rail={rail} activePath={activeNavPath} t={t} />
        </nav>
        <div className={cx('mt-auto flex shrink-0 flex-col gap-2 border-t border-line-soft p-2', rail && 'items-center')}>
          {!rail ? (
            <div className="flex flex-col gap-1.5 rounded-lg border border-line-soft bg-card px-2.5 py-2">
              <span className="truncate text-label text-paper" data-testid="shell-email">
                {displayIdentity}
              </span>
              <Chip tone="signal" lamp={false} className="w-fit">
                {primaryRole !== undefined && primaryRole.length > 0
                  ? primaryRole
                  : t('shell.role.fallback')}
              </Chip>
            </div>
          ) : (
            <span className="sr-only" data-testid="shell-email">
              {displayIdentity}
            </span>
          )}
          <div className={cx('flex items-center gap-1', rail && 'flex-col')}>
            {canChangePassword ? (
              <IconButton
                size="sm"
                label={t('shell.change_password')}
                onClick={() => setChangePasswordOpen(true)}
                data-testid="shell-change-password"
              >
                <KeyRound aria-hidden="true" className="h-4 w-4" />
              </IconButton>
            ) : null}
            <IconButton
              size="sm"
              label={t('shell.logout')}
              onClick={() => {
                void handleLogout();
              }}
              data-testid="shell-logout"
            >
              <LogOut aria-hidden="true" className="h-4 w-4" />
            </IconButton>
            <IconButton
              size="sm"
              label={rail ? t('shell.sidebar.expand') : t('shell.sidebar.collapse')}
              onClick={toggleRail}
              data-testid="shell-rail-toggle"
              className={cx(!rail && 'ml-auto')}
            >
              {rail ? (
                <PanelLeftOpen aria-hidden="true" className="h-4 w-4" />
              ) : (
                <PanelLeftClose aria-hidden="true" className="h-4 w-4" />
              )}
            </IconButton>
          </div>
        </div>
      </aside>

      {/* Mobile nav drawer */}
      <Drawer
        open={mobileNavOpen}
        onOpenChange={setMobileNavOpen}
        title={t('shell.brand.title')}
        width="sm"
        closeLabel={t('shell.nav.close')}
        testId="shell-mobile-nav"
      >
        <nav aria-label={t('shell.nav.aria_label')} className="-mx-2 flex flex-col">
          <NavList groups={navGroups} rail={false} touch={isMobile} activePath={activeNavPath} t={t} />
        </nav>
        <div className="mt-4 flex flex-col gap-3 border-t border-line-soft pt-3">
          <span className="text-micro text-paper-low">{t('shell.account_section')}</span>
          <span className="truncate text-label text-paper">{displayIdentity}</span>
          <Chip tone="signal" lamp={false} className="w-fit">
            {primaryRole !== undefined && primaryRole.length > 0
              ? primaryRole
              : t('shell.role.fallback')}
          </Chip>
          <div className="flex items-center gap-1">
            {isMobile ? (
              <div data-testid="shell-mobile-locale">
                <LocaleSwitcher />
              </div>
            ) : null}
            {canChangePassword ? (
              <IconButton
                size="md"
                label={t('shell.change_password')}
                onClick={() => {
                  setMobileNavOpen(false);
                  setChangePasswordOpen(true);
                }}
                data-testid="shell-mobile-change-password"
              >
                <KeyRound aria-hidden="true" className="h-4 w-4" />
              </IconButton>
            ) : null}
            <IconButton
              size="md"
              label={t('shell.logout')}
              onClick={() => {
                void handleLogout();
              }}
              data-testid="shell-mobile-logout"
            >
              <LogOut aria-hidden="true" className="h-4 w-4" />
            </IconButton>
          </div>
        </div>
      </Drawer>

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {user !== null ? <QuotaBypassBanner isAdmin={isAdmin} /> : null}
        <header className="sticky top-0 z-10 flex h-14 shrink-0 items-center gap-3 border-b border-line-soft bg-canvas px-6 max-lg:px-4 max-md:gap-2 max-md:px-3">
          <IconButton
            label={t('shell.nav.open')}
            onClick={() => setMobileNavOpen(true)}
            className="lg:hidden"
            data-testid="shell-mobile-nav-open"
          >
            <Menu aria-hidden="true" className="h-4 w-4" />
          </IconButton>
          <span className="min-w-0 flex-1 truncate text-section text-paper max-md:min-w-24 md:flex-initial" data-testid="shell-page-title">
            {pageTitle}
          </span>
          <button
            type="button"
            onClick={() => setPaletteOpen(true)}
            data-testid="shell-cmdk-trigger"
            className="ml-2 hidden h-8 min-w-0 max-w-md flex-1 items-center gap-2 rounded border border-line bg-well px-3 text-dense text-paper-low transition-colors duration-120 hover:border-line-strong hover:text-paper-mid focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40 md:flex"
          >
            <Search aria-hidden="true" className="h-3.5 w-3.5 shrink-0" />
            <span className="truncate">{t('shell.cmdk.trigger')}</span>
            <kbd className="ml-auto shrink-0 rounded-sm border border-line px-1.5 font-mono text-micro text-paper-low">
              ⌘K
            </kbd>
          </button>
          <IconButton
            label={t('shell.cmdk.trigger')}
            onClick={() => setPaletteOpen(true)}
            className="md:hidden"
            data-testid="shell-cmdk-trigger-mobile"
          >
            <Search aria-hidden="true" className="h-4 w-4" />
          </IconButton>
          <div className="ml-auto flex shrink-0 items-center gap-3 max-md:gap-1.5">
            <ApprovalsChip enabled={isApprover} />
            {isAdmin ? <RiskChip /> : null}
            {!isMobile ? <LocaleSwitcher /> : null}
          </div>
        </header>
        <main className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-contain p-6 max-lg:p-4 max-md:px-3 max-md:py-4">
          {children ?? <Outlet />}
        </main>
      </div>

      <ShellCommandPalette open={paletteOpen} onOpenChange={setPaletteOpen} />
      {changePasswordOpen ? (
        <ChangePasswordModal onClose={() => setChangePasswordOpen(false)} />
      ) : null}
    </div>
  );
}

interface NavListProps {
  readonly groups: readonly NavGroup[];
  readonly rail: boolean;
  /** Phone drawer rows: ≥ 40px hit target and body type. Desktop sidebar leaves this off. */
  readonly touch?: boolean;
  readonly activePath: string | null;
  readonly t: ReturnType<typeof useT>;
}

function NavList({ groups, rail, touch = false, activePath, t }: NavListProps): JSX.Element {
  return (
    <>
      {groups.map((group) => (
        <div key={group.titleKey} className="flex flex-col gap-0.5">
          {!rail ? (
            <span className="px-2.5 pb-1 pt-4 text-micro text-paper-low">{t(group.titleKey)}</span>
          ) : (
            <span aria-hidden="true" className="mx-3 mb-1 mt-4 h-px bg-line-soft" />
          )}
          {group.items.map((item) => {
            // Longest-match active state (computed once in Shell) so
            // exactly one item lights up on nested routes.
            const isActive = item.to === activePath;
            const link = (
              <Link
                key={item.to}
                to={item.to}
                aria-current={isActive ? 'page' : undefined}
                data-testid={`shell-nav-${item.testId}`}
                className={cx(
                  'flex items-center gap-2.5 rounded px-2.5 font-medium transition-colors duration-120 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40',
                  touch ? 'min-h-10 py-2.5 text-body' : 'py-1.5 text-dense',
                  rail && 'justify-center px-0 py-2',
                  isActive
                    ? 'bg-signal-dim text-signal'
                    : 'text-paper-mid hover:bg-card hover:text-paper',
                )}
              >
                <Icon name={item.icon} size={rail ? 'md' : 'sm'} className="text-current" />
                {!rail ? (
                  <>
                    <span className="min-w-0 flex-1 truncate">{t(item.labelKey)}</span>
                    {isActive ? <StatusLamp tone="signal" size="sm" /> : null}
                  </>
                ) : null}
              </Link>
            );
            return rail ? (
              <Tooltip key={item.to} content={t(item.labelKey)} side="right">
                {link}
              </Tooltip>
            ) : (
              link
            );
          })}
        </div>
      ))}
    </>
  );
}

/**
 * Pending-approvals indicator (epic #1030 E1-S3, #1039), shown only to
 * roles with approver rights (`APPROVAL_QUEUE_ROLES`). Same quiet
 * contract as the RiskChip — nothing while loading, on error (e.g. a
 * sales lead without a sales profile gets 403), or at zero — and the
 * query itself polls on the 30s `refetchInterval` precedent. Clicking
 * deep-links into the approval queue tab.
 *
 * The hook stays mounted-but-disabled for non-approvers so no request
 * ever fires for them.
 */
function ApprovalsChip({ enabled }: { readonly enabled: boolean }): JSX.Element | null {
  const t = useT();
  const navigate = useNavigate();
  const pending = useApprovalQueuePendingCount(enabled);

  if (!enabled || pending.data === undefined || pending.data === 0) {
    return null;
  }
  return (
    <button
      type="button"
      onClick={() => navigate('/approvals/queue')}
      data-testid="shell-approvals-chip"
      aria-label={t('shell.approvals.open_aria')}
      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
    >
      <Chip tone="warn">
        {t('shell.approvals.pending').replace('{count}', String(pending.data))}
      </Chip>
    </button>
  );
}

/**
 * Live risk indicator (admin only): `summary.open` from the unified
 * `GET /v1/ops/risks` endpoint (Epic #853 / #858), so the count covers
 * supply, finance, AND traffic dimensions — the legacy dashboard-alerts
 * four-array sum missed finance and traffic entirely. Quiet by design —
 * nothing while loading or on error, a dim ok lamp when clear, a bad
 * chip linking to the risk queue when anything is open.
 *
 * Since #2685 the balance-source finance facts in that count are the last
 * computed reconciliation, labelled with when it ran (see the body).
 *
 * Since #1736 `summary.open` excludes acked and resolved items, so the chip
 * clears when an operator dispositions the last standing risk. That matters
 * for producers whose steady state is legitimately non-empty (the #1407
 * exclusive-coverage detector): before the fix the chip was red forever and
 * the ack machine had no visible effect here.
 */
function RiskChip(): JSX.Element | null {
  const t = useT();
  const navigate = useNavigate();
  const risks = useOpsRisks();

  if (risks.data === undefined) {
    return null;
  }
  const count = risks.data.summary.open;
  // #2685: the finance fact items behind this count come from the LAST
  // computed balance-source reconciliation — this chip never runs that
  // full-history scan. Its age is always in the tooltip. "Never computed" and
  // "stale" are their own states, because a green chip would claim a check
  // nobody ran recently: past the line (#2686 review M2: ops-api stamps
  // `stale` from contracts BALANCE_SOURCE_CHECK_STALE_AFTER_MS, 7 h — one
  // missed 6-hourly refresh plus grace; this chip holds no threshold of its
  // own) the clear chip turns warn and the age goes on the chip itself.
  const check = risks.data.balanceSourceCheck;
  const computedAt = check?.computedAt ?? null;
  const unchecked = check !== undefined && computedAt === null;
  const stale = !unchecked && check?.stale === true;
  const freshness = check === undefined
    ? null
    : computedAt === null
      ? t('shell.risk.balance_check_never')
      : t('shell.risk.balance_check_at').replace('{at}', formatLocalTimestamp(computedAt));
  const staleSuffix = stale && computedAt !== null
    ? ` · ${t('shell.risk.balance_check_suffix').replace('{at}', formatLocalTimestamp(computedAt))}`
    : '';
  const withFreshness = (chip: JSX.Element): JSX.Element =>
    freshness === null ? chip : <Tooltip content={freshness} side="bottom">{chip}</Tooltip>;

  if (count === 0 && !unchecked && !stale) {
    return withFreshness(
      <span className="inline-flex" data-testid="shell-risk-ok-wrap">
        <Chip tone="ok" testId="shell-risk-ok">
          {t('shell.risk.clear')}
        </Chip>
      </span>,
    );
  }
  const testId = count > 0 ? 'shell-risk-chip' : unchecked ? 'shell-risk-unchecked' : 'shell-risk-stale';
  return withFreshness(
    <button
      type="button"
      onClick={() => navigate('/business/risk')}
      data-testid={testId}
      aria-label={t('shell.risk.open_aria')}
      className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-signal/40"
    >
      {count === 0 ? (
        <Chip tone="warn">
          {unchecked ? (
            t('shell.risk.unchecked')
          ) : (
            <>
              {t('shell.risk.stale')}
              <span className="max-md:hidden">{staleSuffix}</span>
            </>
          )}
        </Chip>
      ) : (
        <Chip tone="bad">
          {t('shell.risk.open').replace('{count}', String(count))}
          <span className="max-md:hidden">
            {unchecked ? ` · ${t('shell.risk.unchecked')}` : staleSuffix}
          </span>
        </Chip>
      )}
    </button>,
  );
}

function QuotaBypassBanner({ isAdmin }: { readonly isAdmin: boolean }): JSX.Element | null {
  const t = useT();
  const bypass = useQuotaBypass();
  if (bypass.data?.enabled !== true) return null;
  const className = 'block border-b border-bad-line bg-bad-dim px-6 py-2 text-dense text-bad max-lg:px-4';
  const text = t('quotas.banner', {
    by: bypass.data.by ?? t('quotas.banner.unknown_operator'),
    at: bypass.data.at ?? '—',
    count: bypass.data.would_reject_count,
  });
  if (isAdmin) {
    return (
      <Link to="/quotas" data-testid="quota-bypass-banner" className={className}>
        {text}
      </Link>
    );
  }
  return (
    <div data-testid="quota-bypass-banner" className={className}>
      {text}
    </div>
  );
}
